import React from 'react';

import { Link, type LinkProps } from '@gitbook/spine-react';

import type { NavigationTarget } from './types';
import { useNavigation } from './useNavigation';

/**
 * A Link component that uses NavigateFn to navigate in app.
 */
export function NavigationLink<TScreenObj extends NavigationTarget>(
    props: React.PropsWithChildren<LinkProps<TScreenObj>>
) {
    const {
        children,
        navigate: propNavigate,
        transformToURL: propTransformToURL,
        ...linkProps
    } = props;
    const { navigate, getNavigationURL } = useNavigation();

    const navigateFn = React.useCallback(
        (to: TScreenObj) => {
            if (propNavigate) {
                return propNavigate(to);
            }

            return navigate(to);
        },
        [navigate, propNavigate]
    );

    const transformToURL = React.useCallback(
        (to: TScreenObj) => {
            if (propTransformToURL) {
                return propTransformToURL(to);
            }

            return getNavigationURL(to);
        },
        [getNavigationURL, propTransformToURL]
    );

    return (
        <Link {...linkProps} navigate={navigateFn} transformToURL={transformToURL}>
            {children}
        </Link>
    );
}
